import { useCallback, useEffect, useRef } from "react";
import { useLocation } from "react-router";
import { useShallow } from "zustand/react/shallow";
import { ModelElementType } from "@/components/entities";
import { FetchListOptions } from "@/components/main/stores/fetchWithInit";
import { useMainStateStore } from "@/components/main/stores/mainStateStore";
import { useAppInitializationStore } from "@/components/main/stores/appInitializationStore";
import { useOrgContextStore } from "@/components/main/stores/orgContextStore";
import { useOnlineStatus } from "@/components/hooks/useOnlineStatus";
import {
  pollingAllowed,
  useResumeRefresh,
} from "@/components/hooks/activityGate";
import { showNotificationGlobal } from "@/stores/useNotificationStore";

// Ambient cadence only — mutations and resume (focus/idle-wake) refetch
// immediately, so this is just the "someone shared with me" catch-up.
const POLL_INTERVAL_MS = 30_000;

export interface ModelsPolling {
  isLoading: boolean;
  isOnline: boolean;
  isAppInitialized: boolean;
  /** True once the list on hand is the active org's, not merely once one landed. */
  modelsInitialized: boolean;
  updateModelList: (
    options?: FetchListOptions,
  ) => Promise<ModelElementType[] | void>;
}

/** Init-fetch + gated ambient polling + resume/online-restore refetch for the
 *  models list. `catalogTrash` reads the trashed catalog models on the same
 *  beats, so the two halves of the catalog page never drift apart. */
export const useModelsPolling = ({
  catalogTrash = false,
}: { catalogTrash?: boolean } = {}): ModelsPolling => {
  const isOnline = useOnlineStatus();
  const intervalRef = useRef<number | null>(null);
  const initializedRef = useRef(false);

  const { isLoading, fetchModelsList, availableModelsOrgId } =
    useMainStateStore(
      useShallow((state) => ({
        isLoading: state.isLoading,
        fetchModelsList: state.fetchModelsList,
        availableModelsOrgId: state.availableModelsOrgId,
      })),
    );
  const { orgsLoaded, activeOrgId } = useOrgContextStore(
    useShallow((s) => ({
      orgsLoaded: s.orgsLoaded,
      activeOrgId: s.activeOrgId,
    })),
  );
  const { isAppInitialized, modelsInitialized } = useAppInitializationStore(
    useShallow((state) => ({
      isAppInitialized: state.isAppInitialized(),
      modelsInitialized: state.modelsInitialized,
    })),
  );

  const updateModelList = useCallback(
    async (options?: FetchListOptions) => {
      if (catalogTrash) void useMainStateStore.getState().fetchCatalogTrash();
      return await fetchModelsList(options);
    },
    [fetchModelsList, catalogTrash],
  );

  // A fetch before the active org is restored would ask for the personal scope.
  const fetchedForOrgRef = useRef<string | null>(null);
  useEffect(() => {
    if (!orgsLoaded) return;
    if (!initializedRef.current && navigator.onLine) {
      initializedRef.current = true;
      fetchedForOrgRef.current = activeOrgId;
      // Without `refresh` this is a cache hit once the store is initialized,
      // so arriving at a list page fetched nothing and waited for the tick.
      void updateModelList({
        showLoading: !isAppInitialized && !modelsInitialized,
        refresh: true,
      });
    }

    intervalRef.current = window.setInterval(() => {
      if (!navigator.onLine || !pollingAllowed()) return;
      void updateModelList({ showLoading: false, refresh: true });
    }, POLL_INTERVAL_MS);

    return () => {
      if (intervalRef.current) {
        clearInterval(intervalRef.current);
        intervalRef.current = null;
      }
    };
  }, [
    updateModelList,
    modelsInitialized,
    isAppInitialized,
    orgsLoaded,
    activeOrgId,
  ]);

  // `force`: an in-flight fetch for the previous org must not be handed back as this one's.
  useEffect(() => {
    if (!initializedRef.current || fetchedForOrgRef.current === activeOrgId)
      return;
    fetchedForOrgRef.current = activeOrgId;
    if (navigator.onLine) {
      void updateModelList({ showLoading: false, refresh: true, force: true });
    }
  }, [activeOrgId, updateModelList]);

  useResumeRefresh(
    useCallback(() => {
      if (navigator.onLine) {
        void updateModelList({ showLoading: false, refresh: true });
      }
    }, [updateModelList]),
  );

  // Immediate fetch when online restores
  const prevOnlineRef = useRef(isOnline);
  useEffect(() => {
    const wasOnline = prevOnlineRef.current;
    prevOnlineRef.current = isOnline;
    if (isOnline && !wasOnline) {
      void updateModelList({ showLoading: false, refresh: true });
    }
  }, [isOnline, updateModelList]);

  return {
    isLoading,
    isOnline,
    isAppInitialized,
    modelsInitialized:
      modelsInitialized && availableModelsOrgId === activeOrgId,
    updateModelList,
  };
};

/** Shows the duplicate-session notice carried via location.state after a
 *  canvas redirect, then clears it from history. */
export const useDuplicateSessionNotice = (): void => {
  const location = useLocation();
  useEffect(() => {
    const state = location.state as { duplicateSession?: boolean } | null;
    if (state?.duplicateSession) {
      showNotificationGlobal(
        "This model is already active in a different tab or window!",
      );
      window.history.replaceState({}, document.title);
    }
  }, [location.state]);
};
